import { Player } from "@/models/players/Player";

// ============================================================================
// CONSTANTES
// ============================================================================

const POSITION = {
  GOALKEEPER: 1,
  DEFENDER: 2,
  MIDFIELDER: 3,
  ATTACKER: 4,
};

const POSITION_LABELS = {
  [POSITION.GOALKEEPER]: "G",
  [POSITION.DEFENDER]: "DEF",
  [POSITION.MIDFIELDER]: "MIL",
  [POSITION.ATTACKER]: "ATT",
};

const FORMATIONS = [
  {
    name: "5-4-1",
    def: 5,
    mid: 4,
    att: 1,
  },
  {
    name: "5-3-2",
    def: 5,
    mid: 3,
    att: 2,
  },
  {
    name: "4-5-1",
    def: 4,
    mid: 5,
    att: 1,
  },
  {
    name: "4-4-2",
    def: 4,
    mid: 4,
    att: 2,
  },
  {
    name: "4-3-3",
    def: 4,
    mid: 3,
    att: 3,
  },
  {
    name: "3-5-2",
    def: 3,
    mid: 5,
    att: 2,
  },
  {
    name: "3-4-3",
    def: 3,
    mid: 4,
    att: 3,
  },
];

const SCORE_WEIGHTS = {
  GOAL: 100,
  OWN_GOAL_PENALTY: - 50,
  NOT_PLAYED_PENALTY: - 1000,
  OWN_GOAL_BASE_PENALTY: - 500,
};

const DEFENSIVE_BONUS = {
  FOUR_DEFENDERS: 0.5,
  FIVE_DEFENDERS: 1,
};

// ============================================================================
// FONCTIONS DE SCORING
// ============================================================================

const getPlayerScore = (player) => {
  if (player.notPlayed) {
    return SCORE_WEIGHTS.NOT_PLAYED_PENALTY;
  }
  if ((player.ownGoals || 0) > 0) {
    return SCORE_WEIGHTS.OWN_GOAL_BASE_PENALTY + (player.rating || 0);
  }
  return (player.goals || 0) * SCORE_WEIGHTS.GOAL + (player.rating || 0);
};

const getTeamScore = (players) => {
  return players.reduce((total, player) => {
    const goals = player.goals || 0;
    const rating = player.rating || 0;
    const ownGoals = player.ownGoals || 0;
    return total + (goals * SCORE_WEIGHTS.GOAL) + rating + (ownGoals * SCORE_WEIGHTS.OWN_GOAL_PENALTY);
  }, 0);
};

const sortByScoreDescending = (players) => {
  return [...players].sort((a, b) => getPlayerScore(b) - getPlayerScore(a));
};

const sortByPositionThenScore = (players) => {
  return [...players].sort((a, b) => {
    const positionDiff = (a.position || 5) - (b.position || 5);
    if (positionDiff !== 0) {
      return positionDiff;
    }
    return getPlayerScore(b) - getPlayerScore(a);
  });
};

// ============================================================================
// EXTRACTION DES DONNÉES JOUEURS
// ============================================================================

const extractPlayerData = (playerId, performances, playersPool) => {
  const lastMatch = performances[performances.length - 1];
  const perf = lastMatch.playerPerformance;

  const playerData = {
    playerId,
    rating: perf.rating,
    goals: perf.goals || 0,
    ownGoals: perf.ownGoals || 0,
    isCaptain: perf.isCaptain || false,
    notPlayed: perf.minutesPlayed === undefined,
    bonusRating: 0,
  };

  if (playersPool) {
    const poolInfo = playersPool.get(playerId);
    if (poolInfo) {
      playerData.firstName = poolInfo.firstName;
      playerData.lastName = poolInfo.lastName;
      playerData.position = poolInfo.position;
      playerData.precisePosition = POSITION_LABELS[poolInfo.position] || "";
    }
  }

  return playerData;
};

const groupPlayersByPosition = (players) => {
  const groups = {
    [POSITION.GOALKEEPER]: [],
    [POSITION.DEFENDER]: [],
    [POSITION.MIDFIELDER]: [],
    [POSITION.ATTACKER]: [],
  };

  players.forEach(player => {
    const position = player.position;
    if (position && groups[position]) {
      groups[position].push(player);
    }
  });

  return groups;
};

// ============================================================================
// BONUS DÉFENSIF
// ============================================================================

const getDefensiveBonus = (defenderCount) => {
  if (defenderCount >= 5) {
    return DEFENSIVE_BONUS.FIVE_DEFENDERS;
  }
  if (defenderCount >= 4) {
    return DEFENSIVE_BONUS.FOUR_DEFENDERS;
  }
  return 0;
};

const applyDefensiveBonus = (players, defenderCount) => {
  const bonus = getDefensiveBonus(defenderCount);
  if (bonus === 0) {
    return players;
  }

  return players.map(player => {
    if (player.position === POSITION.DEFENDER) {
      return {
        ...player,
        bonusRating: bonus,
      };
    }
    return player;
  });
};

// ============================================================================
// SÉLECTION DE FORMATION
// ============================================================================

const canFillFormation = (playersByPosition, formation) => {
  return (
    playersByPosition[POSITION.GOALKEEPER].length >= 1 &&
    playersByPosition[POSITION.DEFENDER].length >= formation.def &&
    playersByPosition[POSITION.MIDFIELDER].length >= formation.mid &&
    playersByPosition[POSITION.ATTACKER].length >= formation.att
  );
};

const selectPlayersForFormation = (playersByPosition, formation) => {
  if (! canFillFormation(playersByPosition, formation)) {
    return null;
  }

  const selected = [
    ...sortByScoreDescending(playersByPosition[POSITION.GOALKEEPER]).slice(0, 1),
    ...sortByScoreDescending(playersByPosition[POSITION.DEFENDER]).slice(0, formation.def),
    ...sortByScoreDescending(playersByPosition[POSITION.MIDFIELDER]).slice(0, formation.mid),
    ...sortByScoreDescending(playersByPosition[POSITION.ATTACKER]).slice(0, formation.att),
  ];

  return {
    formation: formation.name,
    players: selected,
    score: getTeamScore(selected),
  };
};

const findBestFormation = (playersByPosition) => {
  let bestTeam = null;

  for (const formation of FORMATIONS) {
    const team = selectPlayersForFormation(playersByPosition, formation);
    if (team && (! bestTeam || team.score > bestTeam.score)) {
      bestTeam = team;
    }
  }

  return bestTeam;
};

// ============================================================================
// FONCTION PRINCIPALE
// ============================================================================

/**
 * Calcule la meilleure équipe de 11 joueurs à aligner
 * @param {Object} playersPerformances - Performances des joueurs par ID
 * @param {Map} playersPool - Map des joueurs avec leurs infos (nom, poste) - optionnel
 * @returns {Array<Player>} Liste de 11 joueurs triés par position
 */
export function calculateBestTeam (playersPerformances, playersPool = null) {
  const allPlayers = Object.entries(playersPerformances).map(
    ([playerId, performances]) => extractPlayerData(playerId, performances, playersPool),
  );

  const playersByPosition = groupPlayersByPosition(allPlayers);
  const bestTeam = findBestFormation(playersByPosition);

  if (! bestTeam) {
    const fallbackPlayers = sortByScoreDescending(allPlayers).slice(0, 11);
    return fallbackPlayers.map(data => new Player(data));
  }

  // Compter les défenseurs et appliquer le bonus défensif
  const defenderCount = bestTeam.players.filter(p => p.position === POSITION.DEFENDER).length;
  const playersWithBonus = applyDefensiveBonus(bestTeam.players, defenderCount);

  const sortedPlayers = sortByPositionThenScore(playersWithBonus);
  return sortedPlayers.map(data => new Player(data));
}